import {Box, SxProps, ToggleButton, Tooltip} from "@mui/material"
import {Theme} from "@mui/material/styles"
import {MouseEvent, ReactElement, useState} from "react"

import {SxPropsMap} from "../../helper/HelperType"
import {HiddenScrolling} from "./HiddenScrolling"

const ALL = "ALL"
const SX: SxPropsMap = {
    after: {display: "flex", gap: "3px", flexGrow: 1},
    element: {padding: "0px 7px", borderRadius: "3px", lineHeight: "1"},
    count: {width: "26px"},
}

type Props = {
    items: string[],
    selected: string[],
    renderActions?: ReactElement<{sx?: SxProps<Theme>}>[],
    onUpdate: (tags: string[]) => void,
}

export function ToggleButtonScrollable(props: Props) {
    const {items, selected, onUpdate, renderActions} = props
    const tagsSet = new Set(items)
    const [selectedSet, setSelectedSet] = useState(new Set(selected))

    const isAll = selectedSet.has(ALL)
    const count = isAll ? "0" : selectedSet.size.toString()

    return (
        <HiddenScrolling
            arrowHeight={"23px"}
            renderBefore={renderSelectButton(ALL, isAll, handleClickAll)}
            renderAfter={renderAfter()}
        >
            {items.map(tag => renderSelectButton(tag, selectedSet.has(tag), handleClick))}
            {selected.map(tag => renderRemovedButton(tag, !tagsSet.has(tag), handleClick))}
        </HiddenScrolling>
    )

    function renderAfter() {
        return (
            <Box sx={SX.after}>
                {renderInfo()}
                <Tooltip title={renderTagsTooltip()} placement={"top"}>
                    <Box component={"span"}>
                        <ToggleButton
                            sx={[SX.element, SX.count]}
                            size={"small"}
                            color={"secondary"}
                            selected={!isAll}
                            disabled={true}
                            value={count}
                        >
                            {count}
                        </ToggleButton>
                    </Box>
                </Tooltip>
            </Box>
        )
    }

    function renderTagsTooltip() {
        return (
            <Box>
                <Box><b>Tags Selected</b></Box>
                <Box>[ use <b>ctrl</b> to pick more than one tag ]</Box>
            </Box>
        )
    }

    function renderInfo() {
        if (renderActions === undefined) return
        return renderActions
    }

    function renderSelectButton(tag: string, selected: boolean, onClick?: (e: MouseEvent<HTMLElement>, value: string) => void) {
        return (
            <ToggleButton
                sx={SX.element}
                size={"small"}
                color={"secondary"}
                key={tag}
                selected={selected}
                disabled={!onClick}
                value={tag}
                onClick={(e) => onClick?.(e, tag)}
            >
                {tag}
            </ToggleButton>
        )
    }

    function renderRemovedButton(tag: string, removed: boolean, onClick?: (e: MouseEvent<HTMLElement>, value: string) => void) {
        if (tag === ALL || !removed) return null
        return (
            <ToggleButton
                sx={SX.element}
                size={"small"}
                color={"error"}
                key={tag}
                selected={removed}
                disabled={!onClick}
                value={tag}
                onClick={(e) => onClick?.(e, tag)}
            >
                {tag}
            </ToggleButton>
        )
    }

    function handleClick(e: MouseEvent, value: string) {
        const tmp = new Set(selectedSet)
        if (tmp.has(value)) {
            if (e.ctrlKey || tmp.size === 1) {
                tmp.delete(value)
            } else {
                tmp.clear()
                tmp.add(value)
            }
            if (tmp.size === 0) tmp.add(ALL)
        } else {
            tmp.delete(ALL)
            if (e.ctrlKey) {
                tmp.add(value)
            } else {
                tmp.clear()
                tmp.add(value)
            }
        }
        setSelectedSet(tmp)
        onUpdate([...tmp])
    }

    function handleClickAll() {
        const tmp = new Set([ALL])
        setSelectedSet(tmp)
        onUpdate([...tmp])
    }
}
